import React, { useEffect, useRef, useState } from "react";
import ButtonV2 from "../../shared/components/buttonV2";
import {
  CHEVRON_LEFT,
  CLOSE,
  EMPTY_SEARCH_2,
  PROFILE_ADD,
  SEARCH_INPUT_ICON,
} from "../../assets";
import PageTitle from "../../shared/components/pagetitle";
import TableComponent from "../../shared/components/table";
import { useQuery } from "react-query";
import {
  deleteCase,
  deleteUser,
  getAdminUsers,
} from "../../Services/admin.service";
import {
  CASE_COLUMN,
  USER_COLUMN,
} from "../../shared/components/table/columns";
import { SearchHistoryTypes, UserTypes } from "../../types";
import { useAppDispatch, useAppSelector } from "../../hooks";
import {
  selectSelectedUser,
  setDeleteModal,
  showDeleteModal,
} from "../../states/slices/globalReducer";

import ModalV2 from "../../shared/components/modalV2";

import SuccessModal from "../../shared/components/successModal";
import DeleteModal from "../../shared/components/deleteModal";
import { toast } from "react-hot-toast";
import AddUserModal from "../../shared/components/addusersModal";
import { Loader2 } from "lucide-react";

function Users() {
  const [searchQuery, setSearchQuery] = useState("");
  const [currentpage, setCurrentpage] = useState(1);
  const dispatch = useAppDispatch();
  const [uploadCaseModal, setUploadCaseModal] = useState(false);
  const [caseFormModal, setCaseFormModal] = useState(false);

  const {
    data: allUsers,
    refetch,
    isLoading,
  } = useQuery(["users", currentpage], () => getAdminUsers(currentpage));
  const [usersModal, setUsersModal] = useState(false);
  const filterCases = allUsers?.data?.filter((item: UserTypes) =>
    item.name?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const userDetails = useAppSelector(selectSelectedUser);
  const [deleting, setDeleting] = useState(false);

  const [success, setSuccess] = useState(false);
  const pagesRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const deleteModal = useAppSelector(showDeleteModal);
  useEffect(() => {
    const selectedButton = pagesRefs.current[currentpage];
    if (selectedButton) {
      selectedButton.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  }, [currentpage]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const response = await deleteUser(userDetails?.id);
      if (response) {
        toast.success(response?.message);
        dispatch(setDeleteModal(false));
        refetch();
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message);
    } finally {
      setDeleting(false);
    }
  };

  const handleDownloadDoc = (url: string) => {
    const fileName = url.split(".")[1];
    const a = document.createElement("a");
    a.href = url;
    a.download = `${fileName}.xlsx`;
    a.click();
  };

  return (
    <div className="p-3 sm:p-5 w-full">
      <div className="w-full flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <PageTitle title="Users" subtitle="Track all your users" />
        <ButtonV2
          title={"Add New user"}
          image={PROFILE_ADD}
          btnStyle={
            "flex items-center justify-center gap-x-1 bg-globalGold px-3 py-2 rounded-xl flex-row-reverse"
          }
          textStyle={"text-[#FAFAFA] font-medium text-sm"}
          handleClick={() => setUsersModal(true)}
        />
      </div>

      <div className="w-full mt-8 border border-borderColor py-4 rounded-xl">
        <div className="w-full flex flex-col sm:flex-row justify-between items-start sm:items-center px-4 gap-4">
          <span className="text-[#2A2E34] font-semibold text-[16px]">
            All Users
          </span>
          <div className="w-full sm:w-fit items-center gap-x-2 flex px-3 py-2 rounded-xl border border-borderColor">
            <img
              src={SEARCH_INPUT_ICON}
              className="w-[20px] h-[20px] flex-shrink-0"
              alt=""
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-transparent placeholder:text-sm outline-none"
              placeholder="Search users"
            />
          </div>
        </div>

        {allUsers?.data?.length > 0 ? (
          <div className="mt-2">
            <div className="overflow-x-auto">
              <TableComponent
                DATA={filterCases}
                //@ts-ignore
                COLUMNS={USER_COLUMN}
              />
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-between pt-3 px-3 sm:px-5 py-4 gap-4">
              <div className="flex items-center space-x-2 sm:space-x-5">
                <button
                  type="button"
                  onClick={() =>
                    setCurrentpage(allUsers?.meta?.current_page - 1)
                  }
                  disabled={allUsers?.meta?.current_page === 1}
                  className={`border border-[#EBEBEB] rounded-xl w-8 h-8 sm:w-[40px] sm:h-[40px] flex items-center gap-x-2 justify-center ${
                    allUsers?.meta?.current_page === 1 && "opacity-50"
                  }`}
                >
                  <img
                    src={CHEVRON_LEFT}
                    className="w-4 h-4 sm:w-[20px] sm:h-[20px]"
                    alt=""
                  />
                </button>

                <div
                  className={`flex items-center gap-x-1 sm:gap-x-2 ${
                    allUsers?.meta?.links?.slice(1, -1).length > 5 &&
                    "max-w-[150px] sm:max-w-[200px] overflow-x-auto"
                  }`}
                >
                  {allUsers?.meta?.links
                    ?.slice(1, -1)
                    .map((page: any, index: number) => (
                      <button
                        key={index}
                        ref={(el) => (pagesRefs.current[index] = el)}
                        onClick={() => {
                          page.url && setCurrentpage(Number(page.label));
                        }}
                        className={`flex items-center justify-center pt-[0.5px] hover:bg-globalGold hover:text-white transition-all duration-300 gap-x-3 min-w-[32px] h-[32px] sm:min-w-[40px] sm:h-[40px] rounded-xl ${
                          page.active
                            ? "bg-globalGold text-white"
                            : "border text-[#2A2E34]"
                        }`}
                      >
                        <span className="text-xs sm:text-[13px]">
                          {page.label}
                        </span>
                      </button>
                    ))}
                </div>

                <button
                  type="button"
                  onClick={() =>
                    setCurrentpage(allUsers?.meta?.current_page + 1)
                  }
                  disabled={
                    allUsers?.meta?.current_page === allUsers?.meta?.last_page
                  }
                  className={`border border-[#EBEBEB] rounded-xl gap-x-2 w-8 h-8 sm:w-[40px] sm:h-[40px] flex items-center justify-center ${
                    allUsers?.meta?.current_page ===
                      allUsers?.meta?.last_page && "opacity-50"
                  }`}
                >
                  <img
                    src={CHEVRON_LEFT}
                    className="w-4 h-4 sm:w-[20px] sm:h-[20px] scale-x-[-1]"
                    alt=""
                  />
                </button>
              </div>
            </div>
          </div>
        ) : isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 color="#9ca3af" size={30} className="animate-spin" />
          </div>
        ) : (
          <div className="flex flex-col gap-y-2 pb-5 justify-center items-center w-full px-4">
            <img
              src={EMPTY_SEARCH_2}
              className="w-24 h-20 sm:w-[147.69px] sm:h-[120px]"
              alt=""
            />
            <span className="text-lg sm:text-xl font-bold text-customDark text-center">
              No Users Yet
            </span>
            <span className="text-sm font-medium text-primary-50 max-w-96 text-center">
              You haven't Added any users yet. Click the button above to begin
            </span>
          </div>
        )}
      </div>

      <AddUserModal
        open={usersModal}
        close={() => setUsersModal(false)}
        refetch={refetch}
      />

      <ModalV2
        isOpen={uploadCaseModal}
        isClose={() => setUploadCaseModal(false)}
        edges="rounded-xl"
        maxWidth="w-[800px]"
      >
        <div className=" flex items-center w-full p-3 justify-between border-b border-borderColor">
          <span className="text-[#2A2E34] font-semibold text-lg flex-1">
            Upload Case
          </span>

          <button
            onClick={() => setUploadCaseModal(false)}
            className="hover:bg-[#F2F2F2] w-[35px] h-[35px] rounded-full flex items-center justify-center"
            type="button"
          >
            {" "}
            <img src={CLOSE} className="w-[24px] h-[24px]" alt="times" />
          </button>
        </div>
        <div className="flex flex-col gap-y-3 justify-center items-center w-full p-20">
          <ButtonV2
            title={"Upload Case Manually"}
            btnStyle={"w-[230px] px-4 py-2 bg-globalGold rounded-xl"}
            textStyle={"text-sm font-medium text-white"}
            handleClick={() => {
              setUploadCaseModal(false);
              setCaseFormModal(true);
            }}
          />
          <ButtonV2
            title={"Upload by CSV"}
            btnStyle={
              "w-[230px] px-4 border border-borderColor rounded-xl py-2 flex items-center justify-center"
            }
            textStyle={"text-sm font-medium text-customDark"}
            handleClick={function (
              event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
            ): void {
              throw new Error("Function not implemented.");
            }}
          />
          <ButtonV2
            title={"Download CSV Template"}
            btnStyle={
              "w-[230px] px-4 border border-borderColor rounded-xl py-2 flex items-center justify-center"
            }
            textStyle={"text-sm font-medium text-customDark"}
            handleClick={() => handleDownloadDoc("/Lispendes-csv-template.csv")}
          />
        </div>
      </ModalV2>

      <SuccessModal
        open={success}
        close={() => setSuccess(false)}
        action={refetch}
        message={"User Successfully Added"}
        message2={"You have successfully added a user"}
      />
      <DeleteModal
        open={deleteModal}
        close={() => dispatch(setDeleteModal(false))}
        deleteTitle={"Users"}
        warningMessage={`the user “${userDetails?.name}”`}
        lastmessage={"users"}
        handleDelete={handleDelete}
        loading={deleting}
      />
    </div>
  );
}

export default Users;
